import { Link } from "react-router-dom";
import { LANDING_FAQ } from "../data/philosopherPages";

/**
 * Indexable SEO block for the Landing page. Sits below the fold, does not
 * interfere with the hero CTA, and provides ~200 words of readable content
 * for Google + a valid FAQPage schema for rich results.
 */
export default function LandingSEOBlock() {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: LANDING_FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <section
      className="relative z-10 mx-auto mt-20 w-full max-w-2xl px-6 py-16 text-left"
      data-testid="landing-seo-block"
      style={{ borderTop: "1px solid rgba(154,151,164,0.14)" }}
    >
      <p
        className="text-[11px] font-medium uppercase"
        style={{ color: "var(--muted)", letterSpacing: "0.32em" }}
      >
        About the test
      </p>
      <h2
        className="mt-3 font-serif text-[26px] sm:text-[30px] leading-[1.15]"
        style={{ color: "var(--gold)" }}
      >
        Twenty questions. Six traits. Eight philosophers.
      </h2>
      <div className="mt-6 space-y-4 text-[15px] leading-relaxed" style={{ color: "var(--text)" }}>
        <p>
          The Philosopher Test is a free 20-question personality quiz that maps
          your instincts across six traits — Discipline, Curiosity, Resilience,
          Empathy, Ambition and Serenity — and matches you against the
          strongest character in the philosophical canon whose signature is
          closest to yours.
        </p>
        <p>
          There are eight possible matches:{" "}
          <Link to="/philosophers/marcus-aurelius" style={{ color: "var(--gold)" }}>Marcus Aurelius</Link>,{" "}
          <Link to="/philosophers/nietzsche" style={{ color: "var(--gold)" }}>Friedrich Nietzsche</Link>,{" "}
          <Link to="/philosophers/socrates" style={{ color: "var(--gold)" }}>Socrates</Link>,{" "}
          <Link to="/philosophers/epicurus" style={{ color: "var(--gold)" }}>Epicurus</Link>,{" "}
          <Link to="/philosophers/simone-de-beauvoir" style={{ color: "var(--gold)" }}>Simone de Beauvoir</Link>,{" "}
          <Link to="/philosophers/diogenes" style={{ color: "var(--gold)" }}>Diogenes</Link>,{" "}
          <Link to="/philosophers/confucius" style={{ color: "var(--gold)" }}>Confucius</Link>, and{" "}
          <Link to="/philosophers/hannah-arendt" style={{ color: "var(--gold)" }}>Hannah Arendt</Link>.
          Each represents a different way of being a person — a different bet on
          what a good life is made of.
        </p>
        <p>
          The result page is free and includes your six trait scores, your top
          match with a percentage, a ranking of all eight philosophers, and a
          shareable image. There is an optional $9.99 AI-written full report
          for people who want a long-form personal breakdown.
        </p>
      </div>

      <h3
        className="mt-12 font-serif text-[22px]"
        style={{ color: "var(--gold)" }}
        data-testid="landing-faq-title"
      >
        Frequently asked
      </h3>
      <dl className="mt-4 space-y-6" data-testid="landing-faq-list">
        {LANDING_FAQ.map((f) => (
          <div key={f.q}>
            <dt
              className="font-serif text-[17px]"
              style={{ color: "var(--gold-light)" }}
            >
              {f.q}
            </dt>
            <dd
              className="mt-2 text-[14px] leading-relaxed"
              style={{ color: "var(--text)" }}
            >
              {f.a}
            </dd>
          </div>
        ))}
      </dl>

      <script type="application/ld+json">
        {JSON.stringify(faqJsonLd)}
      </script>
    </section>
  );
}
